import { useEffect, useRef, useState } from "react";
import { bool, node } from "prop-types";
import { useStores } from "@hooks";
import { CoreCommands, ProjectCommands, TemplateCommands } from "@client";
import { Manager } from "socket.io-client";
import socketParser from "socket.io-msgpack-parser";
import { changeProjectNameQuery, removeTemplateQuery, saveTemplateQuery } from "@query";
import { WorkspaceSocketContext } from "./WorkspaceSocketContext";

export const WorkspaceSocketProvider = ({ children, isProposal }) => {
  
  const { userStore } = useStores();
  const { messageStore } = useStores();
  
  const [connectionOnline, setConnectionOnline] = useState(false);
  const [socketInitialized, setSocketInitialized] = useState(false);
  
  const socket = useRef(null);
  const manager = useRef(null);
  
  useEffect(() => () => {
    closeConnection();
  }, []);
  
  const init = () => {
    manager.current = new Manager(import.meta.env.APP_COLLAB_SERVICE, {
      path: import.meta.env.APP_COLLAB_SERVICE_WS_PATH,
      host: import.meta.env.APP_COLLAB_SERVICE_WS_HOST,
      transports: ["websocket"],
      parser: socketParser,
    });
    socket.current = manager.current.socket("/");
    setSocketInitialized(true);
  };
  
  const setupWorkspaceEvents = (on) => {
    if(!on) {
      socket.current.removeAllListeners();
      return;
    }
    socket.current.on(CoreCommands.CONNECT_ERROR, onDisconnect);
    socket.current.on(CoreCommands.DISCONNECT, onDisconnect);
    if(!isProposal) {
      socket.current.on(CoreCommands.CONNECTION, onConnect);
      socket.current.on(CoreCommands.JOIN_WORKSPACE, onWorkspaceJoin);
      socket.current.on(CoreCommands.NOTIFICATION, onNotification);
    }
  }
  
  // --------- connections ---------
  
  const closeConnection = () => {
    console.log("CLOSING CONNECTION");
    setConnectionOnline(false);
    socket.current?.close();
  };
  
  const onConnect = () => {
    socket.current.emit(CoreCommands.JOIN_WORKSPACE, {
      companyId: userStore.companyId,
      workspaceUuid: userStore.workspaceUuid,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
      name: userStore.data.fullname,
      email: userStore.data.email,
    });
  };
  
  const onDisconnect = (reason) => {
    console.log("DISCONNECTED", reason);
    setConnectionOnline(false);
  }
  
  const onWorkspaceJoin = (data) => {
    if(data?.joined) {
      console.log("CONNECTED");
      console.log("JOINED WORKSPACE");
      setConnectionOnline(true);
    } else {
      console.error("ERROR WHILE JOINING WORKSPACE: ", data.message)
    }
  }
  
  const onNotification = (messageData) => {
    messageStore.addMessage(messageData);
  }
  
  const sendAction = (command, payload) => {
    if (!socket.current?.connected) return;
    socket.current.emit(command, payload);
  };
  
  const requestProjectRename = async (projectUuid, newName) => {
    await changeProjectNameQuery(projectUuid, newName);
    sendAction(ProjectCommands.PROJECT_RENAME, { projectUuid, newName });
  };
  
  const requestTemplateRename = async (templateId, newName) => {
    await saveTemplateQuery(templateId, { name: newName });
    sendAction(TemplateCommands.TEMPLATE_RENAME, { templateId, newName });
  }
  
  const requestTemplateRemove = async (templateId) => {
    await removeTemplateQuery(templateId);
    sendAction(TemplateCommands.TEMPLATE_REMOVE, { templateId });
  }
  
  const requestWorkTypeCreate = ({ id, name, backgroundColor }) => {
    sendAction(CoreCommands.WORKTYPE_ADD, { id, name, backgroundColor });
  };
  
  const requestWorkTypeChange = ({ id, name, backgroundColor }) => {
    sendAction(CoreCommands.WORKTYPE_EDIT, { id, name, backgroundColor });
  };
  
  const requestWorkTypeRemove = (id) => {
    sendAction(CoreCommands.WORKTYPE_REMOVE, { id });
  };
  
  const requestLabelRemove = (labelId) => {
    sendAction(CoreCommands.LABEL_REMOVE, { labelId });
  };
  
  const requestLabelUpdate = (labelId, labelData) => {
    sendAction(CoreCommands.LABEL_UPDATE, { labelId, labelData });
  };
  
  const requestLabelCreate = (labelData) => {
    sendAction(CoreCommands.LABEL_CREATE, { labelId: labelData.id, labelData });
  };
  
  const value = {
    socket,
    manager,
    connectionOnline,
    setConnectionOnline,
    socketInitialized,
    init,
    setupWorkspaceEvents,
    closeConnection,
    sendAction,
    
    requestProjectRename,
    requestTemplateRename,
    requestTemplateRemove,
    requestWorkTypeCreate,
    requestWorkTypeChange,
    requestWorkTypeRemove,
    requestLabelRemove,
    requestLabelUpdate,
    requestLabelCreate,
  };
  
  return (
    <WorkspaceSocketContext.Provider value={value}>
      {children}
    </WorkspaceSocketContext.Provider>
  );
};

WorkspaceSocketProvider.propTypes = {
  children: node.isRequired,
  isProposal: bool,
};
